import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import {
    Upload, FileText, FolderOpen, Search, CheckCircle,
    XCircle, AlertTriangle, Eye, Trash2, Download,
    ChevronRight, RefreshCw, X, FilePlus, Layers
} from "lucide-react";
import { API_BASE_URL, getBlobRequest } from "../../../../../api";
import {
    confirmPayslipsImportRequest,
    fetchPayslipCoverageRequest,
    previewPayslipsRequest,
    removePayslipPreviewLine,
    resetPayslipImport,
    setPayslipSelectedFiles,
} from "../../../../redux/actions/payslipActions";
import "../parametresStyles/ImportFichePaie.css";

const steps = ["Depot", "Analyse", "Validation", "Import"];

const StatusIcon = ({ status }) => {
    if (status === "VALID") return <CheckCircle size={15} className="fp-icon-ok" />;
    if (status === "WARNING") return <AlertTriangle size={15} className="fp-icon-warn" />;
    return <XCircle size={15} className="fp-icon-err" />;
};

const StatusBadge = ({ status }) => (
    <span className={`fp-badge fp-badge-${status === "VALID" ? "ok" : status === "WARNING" ? "warning" : "error"}`}>
        {status === "VALID" ? "Valide" : status === "WARNING" ? "Avertissement" : "Erreur"}
    </span>
);

const StepsBar = ({ current }) => (
    <div className="fp-steps">
        {steps.map((label, index) => (
            <React.Fragment key={label}>
                <div className={`fp-step ${index < current ? "done" : index === current ? "active" : ""}`}>
                    <div className="fp-step-dot">{index < current ? <CheckCircle size={12} /> : index + 1}</div>
                    <span>{label}</span>
                </div>
                {index < steps.length - 1 && <div className={`fp-step-line ${index < current ? "done" : ""}`} />}
            </React.Fragment>
        ))}
    </div>
);

const formatSize = (bytes) => {
    if (!bytes && bytes !== 0) return "-";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const formatPages = (pageStart, pageEnd) => {
    if (!pageStart && !pageEnd) return "-";
    if (pageStart && pageEnd && pageStart !== pageEnd) return `Pages ${pageStart}-${pageEnd}`;
    return `Page ${pageStart || pageEnd}`;
};

const truncate = (value, limit = 30) => {
    if (!value) return "-";
    return value.length > limit ? `${value.slice(0, limit - 2)}...` : value;
};

const currentMonthValue = () => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
};

export default function ImportFichePaie() {
    const dispatch = useDispatch();
    const {
        selectedFiles,
        previewRows,
        loading,
        loadingAction,
        error,
        confirmSummary,
        coverage,
        coverageError,
        successMessage,
        currentStep,
    } = useSelector((state) => state.payslips);

    const [activeTab, setActiveTab] = useState("import");
    const [coveragePeriod, setCoveragePeriod] = useState(currentMonthValue());
    const [coverageFilter, setCoverageFilter] = useState("all");
    const [coverageQuery, setCoverageQuery] = useState("");
    const [dragging, setDragging] = useState(false);
    const [query, setQuery] = useState("");
    const [filter, setFilter] = useState("all");
    const [preview, setPreview] = useState(null);
    const fileInput = useRef();
    const folderInput = useRef();

    useEffect(() => {
        if (activeTab === "coverage" && coveragePeriod) {
            dispatch(fetchPayslipCoverageRequest(coveragePeriod));
        }
    }, [activeTab, coveragePeriod, dispatch]);

    const addFiles = (newFiles) => {
        const incoming = Array.from(newFiles || []);
        const validFiles = incoming.filter((file) => file?.name?.toLowerCase().endsWith(".pdf"));
        const rejectedFiles = incoming.filter((file) => !file?.name?.toLowerCase().endsWith(".pdf"));

        if (rejectedFiles.length) {
            Swal.fire({
                icon: "warning",
                title: "Fichiers ignores",
                text: "Seuls les fichiers PDF sont acceptes.",
                timer: 1800,
                showConfirmButton: false,
            });
        }

        if (!validFiles.length) {
            return;
        }

        const existingNames = new Set((selectedFiles || []).map((file) => file.name));
        const merged = [
            ...(selectedFiles || []),
            ...validFiles.filter((file) => !existingNames.has(file.name)),
        ];
        dispatch(setPayslipSelectedFiles(merged));
        setPreview(null);
    };

    const removeFileChip = (fileName) => {
        const nextFiles = (selectedFiles || []).filter((file) => file.name !== fileName);
        dispatch(setPayslipSelectedFiles(nextFiles));
    };

    const onDrop = (event) => {
        event.preventDefault();
        setDragging(false);
        addFiles(event.dataTransfer.files);
    };

    const analyse = () => {
        if (!(selectedFiles || []).length) {
            Swal.fire({
                icon: "warning",
                title: "Aucun fichier",
                text: "Ajoutez au moins un fichier PDF avant de lancer l'analyse.",
                confirmButtonText: "OK",
            });
            return;
        }

        dispatch(previewPayslipsRequest(selectedFiles));
    };

    const doImport = () => {
        const validIds = (previewRows || [])
            .filter((row) => row.status === "VALID" && row.tempId)
            .map((row) => row.tempId);

        if (!validIds.length) {
            return;
        }

        dispatch(confirmPayslipsImportRequest(validIds));
    };

    const openPdf = async (relativeUrl, download = false, fileName = "bulletin.pdf") => {
        try {
            const token = sessionStorage.getItem("token");
            const uid = sessionStorage.getItem("uid");
            const response = await getBlobRequest({
                url: `${API_BASE_URL}${relativeUrl}${download ? "?download=true" : ""}`,
                token,
                extraHeaders: { uid },
            });

            const blob = new Blob([response.data], { type: "application/pdf" });
            const blobUrl = window.URL.createObjectURL(blob);

            if (download) {
                const link = document.createElement("a");
                link.href = blobUrl;
                link.download = fileName;
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(blobUrl);
                return;
            }

            window.open(blobUrl, "_blank", "noopener,noreferrer");
            setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60000);
        } catch (downloadError) {
            Swal.fire({
                icon: "error",
                title: "Ouverture impossible",
                text: "Le PDF n'a pas pu etre recupere.",
                confirmButtonText: "OK",
            });
        }
    };

    const stats = useMemo(() => ({
        total: (previewRows || []).length,
        valid: (previewRows || []).filter((row) => row.status === "VALID").length,
        warning: (previewRows || []).filter((row) => row.status === "WARNING").length,
        error: (previewRows || []).filter((row) => row.status === "ERROR").length,
    }), [previewRows]);

    const filteredRows = useMemo(() => {
        const normalizedQuery = query.trim().toLowerCase();
        return (previewRows || []).filter((row) => {
            const matchesQuery =
                !normalizedQuery
                || (row.sourceFileName || row.fileName || "").toLowerCase().includes(normalizedQuery)
                || (row.splitFileName || "").toLowerCase().includes(normalizedQuery)
                || (row.employeeCode || "").toLowerCase().includes(normalizedQuery)
                || (row.employeeFullName || "").toLowerCase().includes(normalizedQuery)
                || (row.detectedEmployeeName || "").toLowerCase().includes(normalizedQuery)
                || (row.periodLabel || "").toLowerCase().includes(normalizedQuery);

            const matchesFilter =
                filter === "all"
                || (filter === "valid" && row.status === "VALID")
                || (filter === "warning" && row.status === "WARNING")
                || (filter === "error" && row.status === "ERROR");

            return matchesQuery && matchesFilter;
        });
    }, [filter, previewRows, query]);

    const analysing = loading && loadingAction === "preview";
    const importing = loading && loadingAction === "confirm";
    const validCount = stats.valid;
    const done = !!confirmSummary && !(selectedFiles || []).length && !(previewRows || []).length;
    const importReport = useMemo(() => {
        const grouped = new Map();
        (confirmSummary?.importedPayslips || []).forEach((row) => {
            const key = row.userId || row.employeeCode || row.employeeFullName || "unknown";
            if (!grouped.has(key)) {
                grouped.set(key, {
                    userId: row.userId,
                    employeeCode: row.employeeCode,
                    employeeFullName: row.employeeFullName || "Collaborateur",
                    payslips: [],
                });
            }
            grouped.get(key).payslips.push(row);
        });

        return Array.from(grouped.values()).map((group) => ({
            ...group,
            payslips: group.payslips.sort((a, b) => String(a.periodDate || a.periodLabel || "").localeCompare(String(b.periodDate || b.periodLabel || ""))),
        }));
    }, [confirmSummary]);
    const coverageRows = useMemo(() => {
        const normalizedQuery = coverageQuery.trim().toLowerCase();
        return (coverage?.rows || []).filter((row) => {
            const matchesFilter =
                coverageFilter === "all"
                || (coverageFilter === "covered" && row.hasPayslip)
                || (coverageFilter === "missing" && !row.hasPayslip);
            const matchesQuery =
                !normalizedQuery
                || (row.employeeFullName || "").toLowerCase().includes(normalizedQuery)
                || (row.employeeCode || "").toLowerCase().includes(normalizedQuery)
                || (row.email || "").toLowerCase().includes(normalizedQuery)
                || (row.jobTitle || "").toLowerCase().includes(normalizedQuery)
                || (row.entityName || "").toLowerCase().includes(normalizedQuery);
            return matchesFilter && matchesQuery;
        });
    }, [coverage, coverageFilter, coverageQuery]);
    const coverageLoading = loading && loadingAction === "coverage";

    return (
        <div className="fp-page">
            <div className="fp-header">
                <div className="fp-header-left">
                    <div className="fp-header-icon"><FileText size={20} /></div>
                    <div>
                        <h1>Import des fiches de paie</h1>
                        <p>Deposez des fichiers, analysez et importez en masse</p>
                    </div>
                </div>
                <div className="fp-header-actions">
                    <button className="fp-btn fp-btn-outline" onClick={() => dispatch(resetPayslipImport())}>
                        <RefreshCw size={13} /> Reinitialiser
                    </button>
                </div>
            </div>

            <div className="fp-tabs">
                <button type="button" className={`fp-tab ${activeTab === "import" ? "active" : ""}`} onClick={() => setActiveTab("import")}>
                    <Upload size={14} /> Import
                </button>
                <button type="button" className={`fp-tab ${activeTab === "coverage" ? "active" : ""}`} onClick={() => setActiveTab("coverage")}>
                    <CheckCircle size={14} /> Suivi des bulletins
                </button>
            </div>

            {activeTab === "import" && (
                <>
                    <div className="fp-steps-wrap">
                        <StepsBar current={currentStep} />
                    </div>

            {!done && (
                <div className="fp-body">
                    <div className="fp-left">
                        <div
                            className={`fp-dropzone ${dragging ? "dragging" : ""}`}
                            onDragOver={(event) => { event.preventDefault(); setDragging(true); }}
                            onDragLeave={() => setDragging(false)}
                            onDrop={onDrop}
                        >
                            <div className="fp-drop-inner">
                                <div className="fp-drop-icon"><Upload size={28} /></div>
                                <p className="fp-drop-title">Glissez vos fiches ici</p>
                                <p className="fp-drop-sub">un fichier, plusieurs, ou un dossier entier</p>
                                <div className="fp-drop-actions">
                                    <button className="fp-btn fp-btn-primary" onClick={() => fileInput.current.click()}>
                                        <FilePlus size={13} /> Fichier(s)
                                    </button>
                                    <button className="fp-btn fp-btn-ghost" onClick={() => folderInput.current.click()}>
                                        <FolderOpen size={13} /> Dossier
                                    </button>
                                </div>
                                <input ref={fileInput} type="file" multiple accept=".pdf,application/pdf" hidden onChange={(event) => addFiles(event.target.files)} />
                                <input ref={folderInput} type="file" webkitdirectory="" hidden onChange={(event) => addFiles(event.target.files)} />
                            </div>
                        </div>

                        {(selectedFiles || []).length > 0 && (
                            <div className="fp-chips-wrap">
                                <div className="fp-chips-header">
                                    <Layers size={13} /> {(selectedFiles || []).length} fichier{(selectedFiles || []).length > 1 ? "s" : ""} chargé{(selectedFiles || []).length > 1 ? "s" : ""}
                                </div>
                                <div className="fp-chips">
                                    {(selectedFiles || []).map((file) => (
                                        <div key={file.name} className="fp-chip">
                                            <FileText size={12} />
                                            <span title={file.name}>{truncate(file.name, 28)}</span>
                                            <button onClick={() => removeFileChip(file.name)}><X size={11} /></button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        <button
                            className={`fp-btn fp-btn-analyse ${(selectedFiles || []).length === 0 ? "disabled" : ""}`}
                            onClick={analyse}
                            disabled={(selectedFiles || []).length === 0 || analysing}
                        >
                            {analysing
                                ? <><span className="fp-spin" />&nbsp;Analyse en cours...</>
                                : <><Eye size={14} /> Analyser & Prévisualiser</>
                            }
                            {!analysing && <ChevronRight size={14} />}
                        </button>
                    </div>

                    <div className="fp-right">
                        {!(previewRows || []).length && !analysing && (
                            <div className="fp-empty-results">
                                <div className="fp-empty-icon"><Eye size={30} /></div>
                                <p>Les résultats d'analyse s'afficheront ici</p>
                                <span>Déposez vos fichiers puis cliquez sur <strong>Analyser & Prévisualiser</strong></span>
                            </div>
                        )}

                        {analysing && (
                            <div className="fp-empty-results">
                                <span className="fp-spin fp-spin-lg" />
                                <p style={{ marginTop: 14 }}>Analyse des fiches...</p>
                            </div>
                        )}

                        {!!error && !(previewRows || []).length && !analysing && (
                            <div className="fp-alert fp-alert-danger">
                                <XCircle size={15} /> {error}
                            </div>
                        )}

                        {(previewRows || []).length > 0 && !analysing && (
                            <>
                                <div className="fp-stats">
                                    <div className="fp-stat"><span className="fp-stat-num">{stats.total}</span><span>Total</span></div>
                                    <div className="fp-stat fp-stat-ok"><span className="fp-stat-num">{stats.valid}</span><span>Valides</span></div>
                                    <div className="fp-stat fp-stat-warn"><span className="fp-stat-num">{stats.warning}</span><span>Avertissements</span></div>
                                    <div className="fp-stat fp-stat-err"><span className="fp-stat-num">{stats.error}</span><span>Erreurs</span></div>
                                </div>

                                {stats.error > 0 && (
                                    <div className="fp-alert fp-alert-danger">
                                        <XCircle size={15} /> {stats.error} fiche{stats.error > 1 ? "s" : ""} en erreur - elles ne seront pas importées.
                                    </div>
                                )}

                                {!!error && (
                                    <div className="fp-alert fp-alert-danger">
                                        <XCircle size={15} /> {error}
                                    </div>
                                )}

                                <div className="fp-res-toolbar">
                                    <div className="fp-search-wrap">
                                        <Search size={13} className="fp-search-icon" />
                                        <input className="fp-search" placeholder="Matricule, nom, fichier, periode..." value={query} onChange={(event) => setQuery(event.target.value)} />
                                    </div>
                                    <div className="fp-filter-tabs">
                                        {[
                                            ["all", "Tous"],
                                            ["valid", "Valides"],
                                            ["warning", "Avert."],
                                            ["error", "Erreurs"],
                                        ].map(([key, label]) => (
                                            <button key={key} className={`fp-filter-tab ${filter === key ? "active" : ""}`} onClick={() => setFilter(key)}>
                                                {label}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                <div className="fp-table-wrap">
                                    <table className="fp-table">
                                        <thead>
                                        <tr>
                                            <th>Statut</th>
                                            <th>Fichier source</th>
                                            <th>Pages</th>
                                            <th>Matricule</th>
                                            <th>Collaborateur</th>
                                            <th>Période</th>
                                            <th>Taille</th>
                                            <th></th>
                                        </tr>
                                        </thead>
                                        <tbody>
                                        {filteredRows.map((row) => (
                                            <tr key={row.tempId || `${row.sourceFileName}-${row.pageStart}-${row.pageEnd}`} className={`fp-row fp-row-${row.status === "VALID" ? "ok" : row.status === "WARNING" ? "warning" : "error"}`}>
                                                <td>
                                                    <button className="fp-icon-btn" title="Voir le detail" onClick={() => setPreview(row)}>
                                                        <StatusIcon status={row.status} />
                                                    </button>
                                                </td>
                                                <td className="fp-td-name" title={row.sourceFileName || row.fileName}>
                                                    {truncate(row.sourceFileName || row.fileName)}
                                                </td>
                                                <td className="fp-muted">{formatPages(row.pageStart, row.pageEnd)}</td>
                                                <td><span className="fp-mono">{row.employeeCode || "-"}</span></td>
                                                <td className="fp-td-emp">{row.employeeFullName || row.detectedEmployeeName || <span className="fp-unknown">Non associe</span>}</td>
                                                <td className="fp-muted">{row.periodLabel || "Non détectée"}</td>
                                                <td className="fp-muted">{formatSize(row.fileSize)}</td>
                                                <td>
                                                    <div className="fp-row-actions">
                                                        {row.downloadUrl && (
                                                            <>
                                                                <button className="fp-icon-btn" title="Previsualiser" onClick={() => openPdf(row.downloadUrl, false, row.splitFileName || row.fileName)}>
                                                                    <Eye size={13} />
                                                                </button>
                                                                <button className="fp-icon-btn" title="Telecharger" onClick={() => openPdf(row.downloadUrl, true, row.splitFileName || row.fileName)}>
                                                                    <Download size={13} />
                                                                </button>
                                                            </>
                                                        )}
                                                        {row.tempId && (
                                                            <button className="fp-icon-btn fp-icon-btn-del" title="Retirer" onClick={() => dispatch(removePayslipPreviewLine(row.tempId))}>
                                                                <Trash2 size={13} />
                                                            </button>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                        </tbody>
                                    </table>
                                </div>

                                <div className="fp-import-bar">
                                    <span className="fp-import-info">
                                        {validCount} fiche{validCount > 1 ? "s" : ""} seront importée{validCount > 1 ? "s" : ""}
                                        {stats.error > 0 && ` · ${stats.error} ignorée${stats.error > 1 ? "s" : ""}`}
                                    </span>
                                    <button
                                        className={`fp-btn fp-btn-primary fp-btn-import ${validCount === 0 ? "disabled" : ""}`}
                                        onClick={doImport}
                                        disabled={validCount === 0 || importing}
                                    >
                                        {importing
                                            ? <><span className="fp-spin" />&nbsp;Import en cours...</>
                                            : <><Upload size={14} /> Confirmer l'import</>
                                        }
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}

            {done && (
                <div className="fp-done">
                    <div className="fp-done-circle"><CheckCircle size={40} /></div>
                    <h2>Import terminé !</h2>
                    <p>{successMessage || "Import des fiches de paie termine."}</p>
                    {!!confirmSummary?.ignoredCount && (
                        <p className="fp-done-warn">{confirmSummary.ignoredCount} fiche{confirmSummary.ignoredCount > 1 ? "s" : ""} ignorée{confirmSummary.ignoredCount > 1 ? "s" : ""}.</p>
                    )}
                    {importReport.length > 0 && (
                        <div className="fp-report">
                            <div className="fp-report-header">
                                <div>
                                    <h3>Rapport d'import</h3>
                                    <span>{confirmSummary.importedCount} bulletin{confirmSummary.importedCount > 1 ? "s" : ""} importe{confirmSummary.importedCount > 1 ? "s" : ""} pour {importReport.length} collaborateur{importReport.length > 1 ? "s" : ""}</span>
                                </div>
                            </div>

                            <div className="fp-report-list">
                                {importReport.map((group) => (
                                    <div className="fp-report-group" key={group.userId || group.employeeCode || group.employeeFullName}>
                                        <div className="fp-report-person">
                                            <div>
                                                <strong>{group.employeeFullName}</strong>
                                                <span>Matricule {group.employeeCode || "-"}</span>
                                            </div>
                                            <span className="fp-report-count">{group.payslips.length}</span>
                                        </div>

                                        <div className="fp-report-periods">
                                            {group.payslips.map((item) => (
                                                <div className="fp-report-period" key={item.payslipId || `${item.periodLabel}-${item.pageStart}-${item.pageEnd}`}>
                                                    <FileText size={13} />
                                                    <span>{item.periodLabel || "Non detectée"}</span>
                                                    <em>{formatPages(item.pageStart, item.pageEnd)}</em>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                    <button className="fp-btn fp-btn-primary" style={{ marginTop: 20 }} onClick={() => dispatch(resetPayslipImport())}>
                        <RefreshCw size={13} /> Nouvel import
                    </button>
                </div>
            )}
                </>
            )}

            {activeTab === "coverage" && (
                <div className="fp-coverage">
                    <div className="fp-coverage-toolbar">
                        <div className="fp-field-inline">
                            <label>Période</label>
                            <input type="month" value={coveragePeriod} onChange={(event) => setCoveragePeriod(event.target.value)} />
                        </div>
                        <button type="button" className="fp-btn fp-btn-outline" onClick={() => dispatch(fetchPayslipCoverageRequest(coveragePeriod))} disabled={coverageLoading || !coveragePeriod}>
                            {coverageLoading ? <span className="fp-spin fp-spin-blue" /> : <RefreshCw size={13} />}
                            Actualiser
                        </button>
                    </div>

                    {coverage && (
                        <div className="fp-coverage-stats">
                            <div className="fp-coverage-stat"><strong>{coverage.totalCollaborators}</strong><span>Collaborateurs</span></div>
                            <div className="fp-coverage-stat ok"><strong>{coverage.coveredCount}</strong><span>Bulletin(s) importé(s)</span></div>
                            <div className="fp-coverage-stat warn"><strong>{coverage.missingCount}</strong><span>Manquant</span></div>
                        </div>
                    )}

                    {coverageError && (
                        <div className="fp-alert fp-alert-danger">
                            <XCircle size={15} /> {coverageError}
                        </div>
                    )}

                    <div className="fp-coverage-panel">
                        <div className="fp-res-toolbar">
                            <div className="fp-search-wrap">
                                <Search size={13} className="fp-search-icon" />
                                <input className="fp-search" placeholder="Collaborateur, matricule, email..." value={coverageQuery} onChange={(event) => setCoverageQuery(event.target.value)} />
                            </div>
                            <div className="fp-filter-tabs">
                                {[
                                    ["all", "Tous"],
                                    ["covered", "Importés"],
                                    ["missing", "Manquants"],
                                ].map(([key, label]) => (
                                    <button key={key} className={`fp-filter-tab ${coverageFilter === key ? "active" : ""}`} onClick={() => setCoverageFilter(key)}>
                                        {label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {coverageLoading && (
                            <div className="fp-empty-results">
                                <span className="fp-spin fp-spin-lg" />
                                <p style={{ marginTop: 14 }}>Chargement du suivi...</p>
                            </div>
                        )}

                        {!coverageLoading && coverageRows.length === 0 && (
                            <div className="fp-empty-results">
                                <div className="fp-empty-icon"><FileText size={30} /></div>
                                <p>Aucun résultat</p>
                                <span>Changez la période ou les filtres.</span>
                            </div>
                        )}

                        {!coverageLoading && coverageRows.length > 0 && (
                            <div className="fp-table-wrap">
                                <table className="fp-table fp-coverage-table">
                                    <thead>
                                    <tr>
                                        <th>Statut</th>
                                        <th>Collaborateur</th>
                                        <th>Matricule</th>
                                        <th>Email</th>
                                        <th>Poste / Entité</th>
                                        <th>Bulletin</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {coverageRows.map((row) => (
                                        <tr key={row.userId || row.employeeCode} className={row.hasPayslip ? "fp-row-ok" : "fp-row-warning"}>
                                            <td>
                                                <span className={`fp-coverage-badge ${row.hasPayslip ? "ok" : "missing"}`}>
                                                    {row.hasPayslip ? "Importé" : "Manquant"}
                                                </span>
                                            </td>
                                            <td className="fp-td-emp">{row.employeeFullName || "-"}</td>
                                            <td><span className="fp-mono">{row.employeeCode || "-"}</span></td>
                                            <td className="fp-muted">{row.email || "-"}</td>
                                            <td className="fp-muted">{[row.jobTitle, row.entityName].filter(Boolean).join(" / ") || "-"}</td>
                                            <td>
                                                {row.hasPayslip ? (
                                                    <div className="fp-report-periods fp-report-periods--table">
                                                        {(row.payslips || []).map((item) => (
                                                            <span className="fp-coverage-period" key={item.payslipId || item.fileName}>
                                                                {item.periodLabel || coverage.periodLabel}
                                                            </span>
                                                        ))}
                                                    </div>
                                                ) : (
                                                    <span className="fp-unknown">Aucun bulletin pour {coverage?.periodLabel || coveragePeriod}</span>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {preview && (
                <div className="fp-overlay" onMouseDown={() => setPreview(null)}>
                    <div className="fp-modal" onMouseDown={(event) => event.stopPropagation()}>
                        <div className="fp-modal-header">
                            <h3>Detail du bulletin</h3>
                            <button className="fp-modal-close" onClick={() => setPreview(null)}><X size={17} /></button>
                        </div>
                        <div className="fp-modal-body">
                            <div className="fp-detail">
                                <div className="fp-detail-status">
                                    <StatusIcon status={preview.status} />
                                    <StatusBadge status={preview.status} />
                                    <span className="fp-detail-msg">{preview.message}</span>
                                </div>
                                <div className="fp-detail-grid">
                                    <div className="fp-detail-item"><span>Fichier source</span><strong>{preview.sourceFileName || preview.fileName || "-"}</strong></div>
                                    <div className="fp-detail-item"><span>Fichier splitte</span><strong>{preview.splitFileName || "-"}</strong></div>
                                    <div className="fp-detail-item"><span>Pages</span><strong>{formatPages(preview.pageStart, preview.pageEnd)}</strong></div>
                                    <div className="fp-detail-item"><span>Taille</span><strong>{formatSize(preview.fileSize)}</strong></div>
                                    <div className="fp-detail-item"><span>Matricule</span><strong className="fp-mono">{preview.employeeCode || "-"}</strong></div>
                                    <div className="fp-detail-item"><span>Collaborateur</span><strong>{preview.employeeFullName || preview.detectedEmployeeName || "-"}</strong></div>
                                    <div className="fp-detail-item"><span>Période</span><strong>{preview.periodLabel || "Non detectée"}</strong></div>
                                </div>
                                {preview.downloadUrl && (
                                    <div className="fp-row-actions" style={{ marginTop: 16 }}>
                                        <button className="fp-btn fp-btn-outline" onClick={() => openPdf(preview.downloadUrl, false, preview.splitFileName || preview.fileName)}>
                                            <Eye size={13} /> Voir le PDF
                                        </button>
                                        <button className="fp-btn fp-btn-outline" onClick={() => openPdf(preview.downloadUrl, true, preview.splitFileName || preview.fileName)}>
                                            <Download size={13} /> Télécharger
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
